import { useTranslation } from 'react-i18next';

import type { DeathCauseApi } from '@/game/domain/deathCause';
import type { GameRun } from '@/game/data/gameRunRepository';
import { DeathCauseSprite } from '@/game/ui/DeathCauseSprite';
import { useIsDarkMode, useThemeColors } from '@/settings/handler/settingsStore';
import { PixelContainer } from '@/ui/pixel/PixelContainer';
import { formatScore } from '@/utils/formatScore';

export function GameRunRow({ run }: { run: GameRun }) {
  const { t } = useTranslation();
  const colors = useThemeColors();
  const isDark = useIsDarkMode();
  const death = deathLabel(t, run.deathCause);

  return (
    <PixelContainer
      radius={3}
      pixelScale={2}
      padding="12px"
      backgroundColor={colors.surfaceContainerHigh}
      borderColor={colors.outline}
    >
      <div className="flex items-center gap-3">
        <div className="min-w-0 flex-1">
          <span className="leading-none" style={{ fontSize: 26, color: colors.onSurface }}>
            {formatScore(run.score)}
          </span>
          <div
            className="mt-1.5 leading-tight"
            style={{ fontSize: 15, color: colors.onSurface, opacity: 0.8 }}
          >
            {t('recentRunKilledBy', { deathCause: death })}
          </div>
          <div
            className="mt-0.5 leading-tight"
            style={{ fontSize: 13, color: colors.onSurface, opacity: 0.5 }}
          >
            {t('recentRunActions', { jumpCount: run.jumpCount, duckCount: run.duckCount })}
          </div>
        </div>

        <PixelContainer
          radius={2}
          pixelScale={2}
          padding="6px 8px"
          backgroundColor={colors.surfaceContainerHighest}
          borderColor={colors.outlineVariant}
        >
          <div className="flex h-11 w-14 items-center justify-center">
            <DeathCauseSprite cause={run.deathCause} isDark={isDark} />
          </div>
        </PixelContainer>
      </div>
    </PixelContainer>
  );
}

export function deathLabel(t: (key: string) => string, cause: DeathCauseApi | string): string {
  switch (cause) {
    case 'pterodactyl':
      return t('deathCausePterodactyl');
    case 'cactus_small_single':
      return t('deathCauseCactusSmallSingle');
    case 'cactus_small_double':
      return t('deathCauseCactusSmallDouble');
    case 'cactus_small_triple':
      return t('deathCauseCactusSmallTriple');
    case 'cactus_large_single':
      return t('deathCauseCactusLargeSingle');
    case 'cactus_large_double':
      return t('deathCauseCactusLargeDouble');
    case 'cactus_large_triple':
      return t('deathCauseCactusLargeTriple');
    case 'cactus_mixed':
      return t('deathCauseCactusMixed');
    default:
      return cause;
  }
}
